import { useCallback, useEffect, useState } from "react";
import {
  getPatientAudio,
  startAudioProcessing,
} from "../../../../../../services/api-documentation";
import { TSession } from "../../../../../../utils/types";
import {
  RecordingUploadQueue,
  recoverChunkedSession,
} from "../services/recordingUploadQueue";
import recordingSessionService from "../services/recordingSessionService";
import {
  ChunkedRecordingResult,
  RecordingSessionRecord,
} from "../types/recording";

type UseRecordingRecoveryParams = {
  session: TSession | null;
  clinicId: string;
  patientId: string;
  onRecovered: (result: ChunkedRecordingResult) => void;
};

const RECOVERABLE_STATUSES: RecordingSessionRecord["status"][] = [
  "stopped",
  "finalizing",
  "failed",
];

export const useRecordingRecovery = ({
  session,
  clinicId,
  patientId,
  onRecovered,
}: UseRecordingRecoveryParams) => {
  const [pendingRecoveryCount, setPendingRecoveryCount] = useState(0);
  const [isRecovering, setIsRecovering] = useState(false);

  const loadPendingRecoveryCount = useCallback(async () => {
    if (!session?.clinicId) {
      setPendingRecoveryCount(0);
      return;
    }

    const sessions = await recordingSessionService.listSessionsByStatus(
      RECOVERABLE_STATUSES,
      session.clinicId,
    );

    setPendingRecoveryCount(
      sessions.filter((item) => item.patientId === patientId).length,
    );
  }, [patientId, session?.clinicId]);

  const recover = useCallback(async () => {
    if (!session || !navigator.onLine) {
      return;
    }

    setIsRecovering(true);
    try {
      const sessions = await recordingSessionService.listSessionsByStatus(
        RECOVERABLE_STATUSES,
        session.clinicId || undefined,
      );

      const patientSessions = sessions.filter(
        (item) => item.patientId === patientId,
      );

      for (const recordingSession of patientSessions) {
        const queue = new RecordingUploadQueue({
          session,
          recordingSession,
          batchSize: recordingSession.recommendedPresignBatchSize,
        });

        // Reconcile with backend missing-part state first, then continue with sequential presigns.
        const matchesSession = await queue.syncMissingParts();
        if (!matchesSession) {
          continue;
        }

        await queue.initialize();
        await queue.flush();

        if (recordingSession.finalPartNumber) {
          await queue.finalize(recordingSession.finalPartNumber);
          // Upload is complete once finalize succeeds; clear local recovery data immediately.
          await recordingSessionService.deleteSessionAndParts(
            recordingSession.id,
          );

          const processed = await startAudioProcessing(
            session,
            recordingSession.audioId,
            recordingSession.patientId,
            recordingSession.noteType,
            recordingSession.appointmentId,
            recordingSession.noteDate,
          );

          const patientAudio = await getPatientAudio(
            session,
            clinicId,
            recordingSession.patientId,
            true,
          );
          const audioMetadata = patientAudio.metadata.find(
            (item) => item.id === recordingSession.audioId,
          );

          onRecovered({
            transcription: processed.transcription,
            summary: processed.summary,
            audioUrl: audioMetadata?.get_url || "",
            audioId: recordingSession.audioId,
            noteType: recordingSession.noteType,
          });
        } else {
          await recoverChunkedSession(session, recordingSession);
        }
      }
    } finally {
      setIsRecovering(false);
      await loadPendingRecoveryCount();
    }
  }, [clinicId, loadPendingRecoveryCount, onRecovered, patientId, session]);

  useEffect(() => {
    loadPendingRecoveryCount();
  }, [loadPendingRecoveryCount]);

  useEffect(() => {
    if (!navigator.onLine || !session?.accessToken) {
      return;
    }

    const handle = setTimeout(() => {
      recover();
    }, 1200);

    return () => clearTimeout(handle);
  }, [recover, session?.accessToken]);

  useEffect(() => {
    const handleOnline = () => {
      recover();
    };

    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [recover]);

  return {
    pendingRecoveryCount,
    isRecovering,
    recover,
  };
};
